import { View, StyleSheet, Pressable, LayoutChangeEvent } from "react-native";
import React, { useEffect, useMemo, useState } from "react";
import { BottomTabBarProps } from "@react-navigation/bottom-tabs";
import { useTheme } from "../hooks/use-theme";
import Animated, {
    Easing,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from "react-native-reanimated";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Text from "./Text";
import { useKeyboardVisible } from "../hooks/keyboard";
import { useIsScreenSizeMin } from "../hooks/useIsScreenSizeMin";
import { ShadowedView, shadowStyle } from "react-native-fast-shadow";
import NotificationsCounter from "./NotificationsCounter";

const AnimatedShadowedView = Animated.createAnimatedComponent(ShadowedView);

export const TAB_BAR_ICON_SIZE = 28;
export const TAB_BAR_ICON_VIEW_SIZE = 30;

const SELECTION_SIZE = 44;
const ANIMATION_DURATION = 300;

const TabBar = ({ state, descriptors, navigation }: BottomTabBarProps) => {
    const { colors, tabBarHeight, gridSize } = useTheme();
    const isKeyboardVisible = useKeyboardVisible();
    const styles = useStyles();
    const nbItems = state.routes.length;
    const isLarge = useIsScreenSizeMin("large");
    const insets = useSafeAreaInsets();

    // Row style (mobile)
    const [viewWidth, setViewWidth] = React.useState(0);

    const selectBarWidth = useMemo(() => {
        if (viewWidth > 0) {
            return viewWidth / nbItems;
        }
        return 0;
    }, [viewWidth, nbItems]);

    const [firstElementX, setFirstElementX] = useState(0);

    const selectBarPosX = useSharedValue(selectBarWidth * state.index);
    const selectRowAnimatedStyles = useAnimatedStyle(() => ({
        left: selectBarPosX.value,
        // width: SELECTION_SIZE, // selectBarWidth - SELECTION_PADDING,
    }));
    useEffect(() => {
        selectBarPosX.value = withTiming(selectBarWidth * state.index + firstElementX, {
            duration: ANIMATION_DURATION,
            easing: Easing.elastic(1),
        });
    }, [state.index, selectBarWidth, selectBarPosX, firstElementX]);

    // Column style (desktop)

    const selectBarHeight = tabBarHeight;

    const selectBarPosY = useSharedValue(selectBarHeight * state.index);
    const selectColumnAnimatedStyles = useAnimatedStyle(() => ({
        top: selectBarPosY.value, // + SELECTION_PADDING / 2,
    }));

    useEffect(() => {
        selectBarPosY.value = withTiming(selectBarHeight * state.index, {
            duration: ANIMATION_DURATION,
            easing: Easing.elastic(1),
        });
    }, [state.index, selectBarHeight, selectBarPosY]);

    const tabBarTranslateY = useSharedValue(0);

    const animatedTabBarStyles = useAnimatedStyle(() => ({
        transform: [{ translateY: tabBarTranslateY.value }],
        marginTop: -tabBarTranslateY.value,
    }));

    useEffect(() => {
        tabBarTranslateY.value = withTiming(isKeyboardVisible ? tabBarHeight : 0);
    }, [isKeyboardVisible, tabBarHeight, tabBarTranslateY]);

    return (
        <AnimatedShadowedView
            style={[
                animatedTabBarStyles,
                styles.container,
                { marginBottom: insets.bottom > 0 ? insets.bottom / 2 : 0 },
                isLarge
                    ? {
                          flexDirection: "column",
                          paddingHorizontal: gridSize / 1.5,
                          height: tabBarHeight * state.routes.length,
                      }
                    : {
                          alignItems: "center",
                          ...shadowStyle({
                              opacity: 0.05,
                              radius: 16,
                          }),
                      },
            ]}
            onLayout={(e) => {
                setViewWidth(e.nativeEvent.layout.width);
            }}
        >
            <Animated.View
                style={[
                    styles.focusedBarStyle,
                    !isLarge ? selectRowAnimatedStyles : selectColumnAnimatedStyles,
                    !isLarge
                        ? {
                              height: SELECTION_SIZE,
                              width: SELECTION_SIZE,
                          }
                        : {
                              height: tabBarHeight,
                              left: gridSize / 3,
                              right: gridSize / 3,
                          },
                ]}
            />

            {state.routes.map((route, index) => {
                const { options } = descriptors[route.key];
                const label =
                    options.tabBarLabel !== undefined
                        ? options.tabBarLabel
                        : options.title !== undefined
                          ? options.title
                          : route.name;
                const icon = options.tabBarIcon;
                const badge = options.tabBarBadge as number;
                const isFocused = state.index === index;

                const onPress = () => {
                    const event = navigation.emit({
                        type: "tabPress",
                        target: route.key,
                        canPreventDefault: true,
                    });

                    if (!isFocused && !event.defaultPrevented) {
                        // The `merge: true` option makes sure that the params inside the tab screen are preserved

                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        navigation.navigate({ name: route.name, merge: true });
                    }
                };

                const onLongPress = () => {
                    navigation.emit({
                        type: "tabLongPress",
                        target: route.key,
                    });
                };

                const onLayout = (e: LayoutChangeEvent) => {
                    if (index === 0) {
                        const { width, x } = e.nativeEvent.layout;
                        setFirstElementX(x - (SELECTION_SIZE - width) / 2);
                    }
                };

                return (
                    <Pressable
                        key={route.key}
                        accessibilityRole="button"
                        accessibilityState={isFocused ? { selected: true } : {}}
                        accessibilityLabel={options.tabBarAccessibilityLabel}
                        testID={options.tabBarButtonTestID}
                        onPress={onPress}
                        onLongPress={onLongPress}
                        style={[
                            styles.buttonStyle,
                            { minHeight: tabBarHeight },
                            isLarge && {
                                flexDirection: "row",
                                justifyContent: "flex-start",
                            },
                        ]}
                    >
                        <View
                            onLayout={onLayout}
                            style={{
                                zIndex: 3,
                                flexDirection: !isLarge ? "column" : "row",
                                justifyContent: "center",
                                alignItems: "center",
                                position: "relative",
                            }}
                        >
                            {icon && (
                                <View style={styles.iconStyle}>
                                    {icon({
                                        focused: true,
                                        color: colors.primary,
                                        size: 30,
                                    })}
                                </View>
                            )}
                            {isLarge && label && (
                                <Text
                                    allowFontScaling={false}
                                    numberOfLines={1}
                                    style={[
                                        styles.textStyle,
                                        {
                                            color: isFocused ? colors.primary : colors.primary,
                                        },
                                        isLarge && {
                                            marginLeft: 14,
                                            fontFamily: "Inter-SemiBold",
                                            fontSize: 14,
                                        },
                                    ]}
                                >
                                    {label as string}
                                </Text>
                            )}
                        </View>
                        {badge !== undefined && (
                            <NotificationsCounter
                                count={badge}
                                style={[
                                    { top: 13, right: 7 },
                                    isLarge && {
                                        top: 15,
                                        left: 23,
                                    },
                                ]}
                            />
                        )}
                    </Pressable>
                );
            })}
        </AnimatedShadowedView>
    );
};

const useStyles = () => {
    const { colors, roundness, tabBarHeight, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    position: "relative",
                    flexDirection: "row",
                    backgroundColor: colors.background,
                    height: tabBarHeight,
                },
                buttonStyle: {
                    position: "relative",
                    flex: 1,
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                },
                textStyle: {
                    ...fonts.labelSmall,
                },
                iconStyle: {
                    alignItems: "center",
                    justifyContent: "flex-end",
                },
                focusedBarStyle: {
                    position: "absolute",
                    borderRadius: roundness,
                    backgroundColor: colors.accentLighter,
                    zIndex: -1,
                },
                unreadStyle: {},
            }),
        [colors.accentLighter, colors.background, fonts.labelSmall, roundness, tabBarHeight],
    );
};

export default TabBar;
